import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 1,
  class: "item-row-heading"
}
const _hoisted_3 = {
  key: 0,
  class: "wc-product-row-modern__badge"
}
const _hoisted_4 = {
  key: 0,
  class: "wc-item-row__spinner-wrapper d-flex"
}

import axios from 'axios';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import WCProductCardModern from '@/components/WCProductCardModern/WCProductCardModern.vue';
import WCCarouselModern from '@/components/WCCarouselModern/WCCarouselModern.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import { ENTRY_METHOD, type EntryMethod } from '@/models/entities/cart/entry-method';
import usePlatform from '@/modules/platform/composables/usePlatform';
import useCardLimitInRow, {
  DEFAULT_CARD_WIDTH_WITH_MARGIN,
  DEFAULT_BUFFER_CARDS,
} from '@/composables/useCardLimitInRow';
import type ItemDetail from '@/models/entities/items/item-detail';

interface Props {
  rowId: string;
  rowName: string;
  rowType?: string;
  rowSizeLimit?: number;
  hideIfEmpty?: boolean;
  entryMethod?: EntryMethod;
  cardWidthWithMargin?: number;
  bufferCards?: number;
  items?: ItemDetail[];
  totalCount?: number;
  seeAllRoute?: string;
  isPersonalized?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WCProductRowModern',
  props: {
    rowId: {},
    rowName: {},
    rowType: { default: undefined },
    rowSizeLimit: { default: 7 },
    hideIfEmpty: { type: Boolean, default: true },
    entryMethod: { default: ENTRY_METHOD.DEFAULT },
    cardWidthWithMargin: {},
    bufferCards: { default: DEFAULT_BUFFER_CARDS },
    items: { default: undefined },
    totalCount: { default: undefined },
    seeAllRoute: { default: undefined },
    isPersonalized: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const store = useStore();
const loading = ref(false);
const loadFailedError = ref<string | null>(null);
const totalCount = ref(-1);
const products = ref<ItemDetail[]>([]);
const rowRef = ref<HTMLElement | null>(null);
const titleRef = ref<HTMLElement | null>(null);
const hasThemeTitleOverride = ref(false);
const { platform } = usePlatform();
const productRowClass = ref('product-row-class-desktop');
const productRowCarouselClass = ref('wc-product-row-modern__carousel-desktop');
const { t } = useI18n();
const getUser = computed(() => store.getters['user/getUser']);
const isControlled = computed(() => props.items !== undefined);

const viewMoreLink = computed(() => {
  return props.seeAllRoute || `/b?g=${props.rowId}`;
});

const getCardWidthWithMargin = (): number => {
  if (!rowRef.value) {
    return DEFAULT_CARD_WIDTH_WITH_MARGIN;
  }
  const style = getComputedStyle(rowRef.value);
  const cardWidth = parseFloat(style.getPropertyValue('--wc-row-card-width'));
  const gap = parseFloat(style.getPropertyValue('--wc-row-card-gap'));
  return cardWidth && gap ? cardWidth + gap : DEFAULT_CARD_WIDTH_WITH_MARGIN;
};

const getCardLimitInRow = useCardLimitInRow(
  rowRef,
  () => props.rowSizeLimit,
  () => props.cardWidthWithMargin ?? getCardWidthWithMargin(),
  () => props.bufferCards,
);

const fetchData = async (): Promise<void> => {
  const ps = platform.value.isMobile ? props.rowSizeLimit : getCardLimitInRow();
  const req: Record<string, unknown> = {
    ps,
    pn: 1,
  };

  if (props.rowType === 'coupon') {
    req.facets = { cp: [props.rowId] };
  } else {
    req.g = [props.rowId];
  }

  try {
    const response = await axios.post('api/b/', req);
    products.value = response.data.items;
    totalCount.value = response.data.totalCount
      ? response.data.totalCount
      : response.data.items.length;

    AnalyticsService.track(TrackableEventConstants.ItemImpression, {
      items: products.value,
      customerId: getUser.value?.sub,
      listId: props.rowId,
      listName: props.rowName,
    });
  } catch (error) {
    loading.value = false;
    loadFailedError.value = error instanceof Error ? error.message : String(error);
    console.error(error);
  }
};

const updateData = async () => {
  totalCount.value = 0;
  products.value = [];
  loading.value = true;
  loadFailedError.value = null;
  await fetchData();
  loading.value = false;
};

watch(() => props.rowId, () => {
  if (!isControlled.value) {
    updateData();
  }
});

watch(
  () => props.items,
  (newItems) => {
    if (newItems !== undefined) {
      products.value = newItems;
      totalCount.value = props.totalCount ?? newItems.length;
      loading.value = false;
      loadFailedError.value = null;
    }
  },
  { immediate: true },
);

const updateProductRowClass = () => {
  if (platform.value.isMediumDevice) {
    productRowClass.value = 'product-row-class-tablet';
    productRowCarouselClass.value = 'wc-product-row-modern__carousel-tablet';
  } else if (platform.value.isMobile) {
    productRowClass.value = 'product-row-class-mobile';
    productRowCarouselClass.value = 'wc-product-row-modern__carousel-mobile';
  } else {
    productRowClass.value = 'product-row-class-desktop';
    productRowCarouselClass.value = 'wc-product-row-modern__carousel-desktop';
  }
};

// theme.css may inject a title via `h2::after { content: "..." }`.
// When it does, the original span is hidden so it doesn't reserve layout space next to it.
const checkThemeTitleOverride = () => {
  if (!titleRef.value) {
    hasThemeTitleOverride.value = false;
    return;
  }
  const { content } = window.getComputedStyle(titleRef.value, '::after');
  hasThemeTitleOverride.value = Boolean(content) && content !== 'none' && content !== 'normal';
};

if (props.isPersonalized) {
  watch(titleRef, (el) => {
    if (!el) {
      hasThemeTitleOverride.value = false;
      return;
    }
    checkThemeTitleOverride();
  });
}

onMounted(() => {
  if (!isControlled.value) {
    updateData();
  }
  updateProductRowClass();
  window.addEventListener('resize', updateProductRowClass);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateProductRowClass);
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["item-row", productRowClass.value]),
    ref_key: "rowRef",
    ref: rowRef
  }, [
    (!_ctx.hideIfEmpty || products.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(productRowClass.value)
        }, [
          (loadFailedError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(loadFailedError.value), 1))
            : _createCommentVNode("", true),
          _cache[3] || (_cache[3] = _createTextVNode()),
          (!loadFailedError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("h2", {
                  class: "wc-product-row-modern__title",
                  ref_key: "titleRef",
                  ref: titleRef
                }, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["wc-product-row-modern__title-text", {
                'wc-product-row-modern__title-text--hidden':
                  _ctx.isPersonalized && hasThemeTitleOverride.value,
              }])
                  }, _toDisplayString(_ctx.rowName), 3)
                ], 512),
                _cache[0] || (_cache[0] = _createTextVNode()),
                (_ctx.isPersonalized)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(t)('personalized')), 1))
                  : _createCommentVNode("", true),
                _cache[1] || (_cache[1] = _createTextVNode()),
                (totalCount.value > products.value.length)
                  ? (_openBlock(), _createBlock(_component_router_link, {
                      key: 1,
                      to: viewMoreLink.value,
                      class: "wc-product-row-modern__view-more-link"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('seeAll')), 1)
                      ]),
                      _: 1
                    }, 8, ["to"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _cache[4] || (_cache[4] = _createTextVNode()),
          (!loadFailedError.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                (loading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createVNode(WCSpinner, {
                        class: "mx-auto align-self-center",
                        size: "xl"
                      })
                    ]))
                  : _createCommentVNode("", true),
                _cache[2] || (_cache[2] = _createTextVNode()),
                (!loading.value)
                  ? (_openBlock(), _createBlock(WCCarouselModern, {
                      key: 1,
                      class: _normalizeClass(productRowCarouselClass.value),
                      title: _ctx.rowName,
                      showArrows: true
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(products.value, (product) => {
                          return (_openBlock(), _createBlock(WCProductCardModern, {
                            key: product.id,
                            item: product,
                            class: "wc-product-row-modern__card",
                            role: "listitem"
                          }, null, 8, ["item"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["class", "title"]))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true)
  ], 2))
}
}

})